import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IQuestionSubType } from "./IQuestionSubType";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import { EditQuestionSubType } from "../../Redux/Slices/QuestionSubType/EditQuestionSubTypeSlice";
import { addQuestionSubType } from "../../Redux/Slices/QuestionSubType/AddQuestionSubTypeSlice";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import { listQuestionType } from "../../Redux/Slices/QuestionType/ListQuestionTypeSlice";

interface QuestionSubTypeFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IQuestionSubType;
  setInitialValues: React.Dispatch<React.SetStateAction<IQuestionSubType>>;
}

const QuestionSubTypeForm: FunctionComponent<QuestionSubTypeFormProps> = (
  props
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(listQuestionType());
  }, [dispatch]);

  const { data: listQuestionTypes } = useAppSelector(
    (state) => state.ListQuestionTypeSlice
  );

  const QuestionTypeList = Array.isArray(listQuestionTypes?.data)
    ? listQuestionTypes?.data.map((item) => ({
      label: item.name!,
      value: item.id,
    }))
    : [];

  const userSchema = z.object({
    id: z.number().optional(),
    subtype_name: z
      .string({ message: "Please enter question subtype name" })
      .min(1, { message: "Please enter question subtype name" })
      .max(50, { message: "Question subtype name cannot exceed 50 characters" }),
    questiontype_id: z.coerce.number({
      required_error: "Please select question type",
      invalid_type_error: "Please select question type",
    }).min(1, { message: "Please select question type" }),
    sequence: z.coerce
      .number({ message: "Please enter sequence" })
      .min(1, { message: "Sequence must be at least 1" })
      .max(100, { message: "Sequence cannot exceed 100" })
      .optional()
      .nullable(),
    status: z.boolean({
      message: "Please select the status",
    }),
    description: z
      .string()
      .max(250, { message: "Description cannot exceed 250 characters" })
      .optional()
      .nullable()
      .or(z.literal("")),
  });

  const methods = useForm<IQuestionSubType>({
    defaultValues: {
      ...props.initialValues,
      status: props.initialValues.id ? props.initialValues.status : true,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (props.initialValues) {
      methods.reset({
        ...props.initialValues,
        status: props.initialValues.id ? props.initialValues.status : true,
      });
    }
  }, [props.initialValues, methods]);

  const onSubmit: SubmitHandler<IQuestionSubType> = (val) => {
    if (val.id) {
      dispatch(EditQuestionSubType(val));
    } else {
      dispatch(addQuestionSubType(val));
    }
  };

  const statusOptions = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        <TextInputFieldC
          name="subtype_name"
          label="Question Sub Type"
          type="text"
        />
        <Select
          name="questiontype_id"
          label="Question Type"
          options={QuestionTypeList}
          value={
            QuestionTypeList?.find(
              (option) => Number(option.value) === Number(methods.watch("questiontype_id"))
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("questiontype_id", val?.value as number, {
              shouldValidate: true,
            });
          }}
        />
        <NumberInputField name="sequence" label="Sequence" type="number" />
        <Select
          name="status"
          label="Status"
          options={statusOptions}
          value={
            statusOptions.find(
              (option) => Boolean(option.value) === Boolean(methods.watch("status"))
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value as boolean, {
              shouldValidate: true,
            });
          }}
        />
        <TextAreaInputField
          name="description"
          label="Description"
          type="text"
        />
      </form>
    </FormProvider>
  );
};

export default memo(QuestionSubTypeForm);

